import * as angular from 'angular';
import {
    Attribute, BoundingBox, CommonAssociationFeature,
    NvdbFeature, Stedfesting
} from '../../presentation/routes/contract-features-3/domain';
import server from '../server/server-services';
import {IHttpPromise, IHttpResponse, IPromise} from 'angular';
import {FeatureGeometry} from '../representation/feature';
import {AttributeType} from '../datacatalog/datacatalog-services';
import {LatLng} from 'leaflet';
import {Point} from 'geojson';
import WKT from 'terraformer-wkt-parser';
import MapUtils from '../../presentation/map/map-utils';
import {chunkArray} from "../../array";

interface NvdbEgenskap {
    id: number;
    navn: string;
    datatype: number;
    egenskapstype: string;
    verdi: string | number;
}

interface nvdbFeatureType {
    id: number;
    href: string;
    metadata: {
        type: {
            id: number;
            navn: string
        }
        versjon: number;
        startdato: string;
        sluttdato: string;
        sist_modifisert: string;
    };
    egenskaper: NvdbEgenskap[];
    geometri: {
        wkt: string;
        srid: number;
        egengeometri: boolean;
    };
    relasjoner: {
        foreldre: {
            type: {
                id: number;
                navn: string;
            },
            vegobjekter: {
                id: string;
            }[]
        }[]
        barn: {
            type: {
                id: number;
                navn: string,
            },
            vegobjekter: {
                id: string;
            }[]
        }[]
    };
    lokasjon: {
        stedfestinger: {
            veglenkesekvensid: number,
            startposisjon: number,
            sluttposisjon: number,
            kortform: string,
            retning: string,
            sideposisjon: string
        }[],
        vegsystemreferanser: {
            kortform: string,
            [p: string]: any
        }[]
    };
    missingAttributeTypes: AttributeType[];
}

export type VegobjekterResponse = {
    metadata: {
        returnert: number,
        neste: {
            href: string,
            start: string
        }
    };
    objekter: nvdbFeatureType[]
};

export interface Fylke {
    nummer: number;
    navn: string;
}


// https://nvdbapiles-v3.atlas.vegvesen.no/dokumentasjon/openapi/#/Vegnett/get_beta_vegnett_rute
export type VegRespons = {
    vegsystemreferanse: {
        kortform: string;
    }
};

export interface PointFromVegSystemReferanse {
    geometri: {
        wkt: string;
        srid: number;
    };
}

export interface PositionShortForm {
    veglenkesekvens: {
        kortform: string;
    };
}

export interface Route {
    vegnettsrutesegmenter: PointFromVegSystemReferanse[];
}

const HEADERS_V3: {Accept: string, 'X-Client': string, 'If-Modified-Since'?: string} = {
    'Accept': 'application/vnd.vegvesen.nvdb-v3-rev1+json',
    'X-Client': 'Datafangst',
    'If-Modified-Since': undefined
};

function toFeatureGeometry(wkt: string): FeatureGeometry {
    return {
        wkt,
        length: -1,
        measurementMethod: 'Ukjent',
        measurementMethodHeight: 'Ukjent',
        precision: -1,
        precisionHeight: -1,
        operation: 'READ',
        datafangstdato: null,
        heightReference: null,
    };
}

export default class nvdbapi {
    private readonly $http: angular.IHttpService;
    private readonly server: server;
    private nvdbReadApiUrl: string;

    constructor($http: angular.IHttpService, server: server) {
        this.$http = $http;
        this.server = server;
        server.getNvdbReadApiUrl().then(url => {
            this.nvdbReadApiUrl = url;
        });
    }

    getObjectsOfTypeInArea(typeId: number, searchArea: BoundingBox, includeRelations?: boolean): Promise<NvdbFeature[]> {
        const includeProps = 'metadata,egenskaper,geometri,lokasjon' + (includeRelations ? ',relasjoner' : '');
        const x1 = searchArea.southWest.lng;
        const y1 = searchArea.southWest.lat;
        const x2 = searchArea.northEast.lng;
        const y2 = searchArea.northEast.lat;
        const srid = searchArea.srid;
        return new Promise((resolve, reject) => {
            this.$http<VegobjekterResponse>({
                url: '/rest/vegobjekter?typeId=' + typeId + '&x1=' + x1 + '&y1=' + y1 + '&x2=' + x2 + '&y2='
                    + y2 + '&srid=' + srid + '&include=' + includeProps,
                method: 'GET',
                description: 'Søke etter vegobjekter fra NVDB'
            } as angular.IRequestConfig)
                .then(
                    (response) => {
                        const srid = 'UTM33';
                        const objekter = response.data.objekter;
                        resolve(this.transformToFeatures(objekter, srid));
                    },
                    (error) => reject(error)
                );
        });
    }

    private transformToFeatures(objekter: nvdbFeatureType[], srid: string) {
        return objekter.map(feature => this.transformFeature(feature, srid));
    }

    private transformFeature(feature: nvdbFeatureType, srid: string): NvdbFeature {
        let egenskaper: Attribute[] = [];
        if (feature.hasOwnProperty('egenskaper') && feature.egenskaper != null && feature.egenskaper.length > 0) {
            egenskaper = feature.egenskaper.map(e => nvdbapi.transformAttribute(e));
        }
        const name = feature.metadata.type.navn + feature.id;

        let stedfestinger;
        if (feature.lokasjon?.stedfestinger) {
            stedfestinger = feature.lokasjon.stedfestinger.map(stedfesting => {
                const {veglenkesekvensid, startposisjon, sluttposisjon, sideposisjon, kortform, retning} = stedfesting;
                return new Stedfesting(veglenkesekvensid, startposisjon, sluttposisjon, kortform, retning, sideposisjon);
            });
        }

        return new NvdbFeature(
            feature.id.toString(10),
            name,
            name,
            feature.metadata.type.id,
            new Date(feature.metadata.sist_modifisert),
            false,
            egenskaper,
            toFeatureGeometry(feature.geometri.wkt),
            feature.metadata.versjon,
            feature.metadata.startdato,
            feature.metadata.sluttdato,
            srid,
            (feature.relasjoner as any),
            stedfestinger,
            feature.missingAttributeTypes
        );
    }

    toCommonAssociationFeature(feature: nvdbFeatureType): CommonAssociationFeature {
        let egenskaper: Attribute[] = [];
        if (feature.hasOwnProperty('egenskaper') && feature.egenskaper != null && feature.egenskaper.length > 0) {
            egenskaper = feature.egenskaper.map(e => nvdbapi.transformAttribute(e));
        }

        const name = feature.metadata.type.navn + feature.id;
        return new CommonAssociationFeature(
            feature.id.toString(10),
            name,
            name,
            name, // fallback to name instead of descriptiveName from NVDB
            feature.metadata.type.id,
            egenskaper,
            toFeatureGeometry(feature.geometri.wkt),
            feature.metadata.versjon,
            'NN54',
            feature.geometri.srid,
            (feature.relasjoner as any),
            feature.missingAttributeTypes
        );
    }


    private static transformAttribute(e: NvdbEgenskap): Attribute {
        let verdiString;
        if (typeof e.verdi ===  'number') {
            verdiString = e.verdi.toString();
        } else {
            verdiString = e.verdi;
        }

        return new Attribute(e.navn, null, verdiString, e.id, {notabenes: [], errors: [], warnings: []}, 'READ');
    }

    getVegobjekt(typeId: number, featureId: number): IPromise<nvdbFeatureType> {
        return this.$http<nvdbFeatureType>({
            url: this.nvdbReadApiUrl + `/vegobjekter/${typeId}/${featureId}`,
            params: {
                srid: 'UTM33'
            },
            headers: HEADERS_V3,
            method: 'GET',
            description: 'Lese vegobjekt ' + featureId + ' fra NVDB'
        } as angular.IRequestConfig)
            .then(result => result.data);
    }

    getVegobjekterByIds(typeId: number, featureIds: number[]): Promise<nvdbFeatureType[]> {

        if(featureIds.length === 0){
            return Promise.resolve([]);
        }

        const perChunk = 155 ;

        const featureChunks = chunkArray(featureIds, perChunk);

        return Promise.all<nvdbFeatureType | null>(featureChunks.map((chunk) => {
            return this.$http<{objekter: nvdbFeatureType[]}>({
                url: this.nvdbReadApiUrl + `/vegobjekter/${typeId}`,
                params: {
                    ider: chunk.join(','),
                    srid: 'UTM33',
                    inkluder: 'lokasjon'
                },
                headers: HEADERS_V3,
                method: 'GET',
                description: 'Lese ' + chunk.length + ' vegobjekter fra NVDB'
            } as angular.IRequestConfig)
                .then(chunkResult => {
                    if(chunkResult.status < 299){
                        return chunkResult.data.objekter;
                    } else {
                        return null;
                    }
                }, () => null)
        })).then(arrs => arrs.reduce((prev, curr) => curr !== null ? prev.concat(curr) : prev, []));

    }

    getVegSystemReferanseFromRefLink(refLink: string) {
        const noRef: VegRespons = {
            vegsystemreferanse: {
                kortform: ''
            }
        };
        if (refLink.includes('undefined')) {
            return Promise.resolve(noRef);
        }
        return this.$http<VegRespons>({
            url: this.nvdbReadApiUrl + `/veg`,
            params: {
                veglenkesekvens: refLink
            },
            headers: HEADERS_V3,
            method: 'GET',
            description: 'Lese veg fra referanselenke'
        } as angular.IRequestConfig)
            .then(result => result.data)
            .catch(() => noRef);
    }

    getCounties(): IHttpPromise<Fylke[]> {
        if (!this.nvdbReadApiUrl) {
            return this.server.getNvdbReadApiUrl().then(host => {
                this.nvdbReadApiUrl = host;
                return this.counties();
            });
        } else {
            return this.counties();
        }

    }

    counties(): IHttpPromise<Fylke[]> {
        return this.$http({
            url: this.nvdbReadApiUrl + '/omrader/fylker',
            headers: HEADERS_V3,
            method: 'GET',
            description: 'Lese fylker'
        } as angular.IRequestConfig);
    }

    getFeatureUri(featureTypeId: number, nvdbId: number): string {
        return `${this.nvdbReadApiUrl}/vegobjekter/${featureTypeId}/${nvdbId}`;
    }

    getGeometryFromReferences(references: string[]): Promise<IHttpResponse<PointFromVegSystemReferanse>[]> {
        return Promise.all(references.map(reference => this.$http<PointFromVegSystemReferanse>({
            url: `${this.nvdbReadApiUrl}/veg?vegsystemreferanse=${reference}&srid=wgs84`,
            headers: HEADERS_V3,
            method: 'GET',
            description: 'Hente punkt fra vegsystemreferanse'
        } as angular.IRequestConfig)));
    }

    getPointsFromReferences(references: string[]): Promise<LatLng[]> {
        return this.getGeometryFromReferences(references).then(results => results.filter(result => {
            return result.status === 200 && WKT.parse(result.data.geometri.wkt).type === 'Point';
        }).map(result => {
            const parsed = WKT.parse(result.data.geometri.wkt) as Point;
            const [lng, lat] = parsed.coordinates;
            return new LatLng(lat, lng);
        }));
    }

    getRangeOfSystemRefs(before: string, startAt: number, endAt: number) {
        const maxNumberOfCoordinates = 20;
        let numberOfCoordinates = endAt - startAt;
        let intervals = 1;
        // Won't do more than 20 requests to Les.
        if (numberOfCoordinates > 20) {
            numberOfCoordinates = 20;
            intervals = Math.floor((endAt - startAt) / (maxNumberOfCoordinates - 2));
        }
        return [...Array(numberOfCoordinates).keys()].map(i => {
            if (i === numberOfCoordinates - 1) {
                return before + endAt;
            } else {
                return before + (i * intervals + startAt);
            }
        });
    }

    getShortFormFromSystemReference(systemReference: string): IPromise<string> {
        return this.$http<PositionShortForm>({
            url: `${this.nvdbReadApiUrl}/veg?vegsystemreferanse=${systemReference}`,
            headers: HEADERS_V3,
            method: 'GET',
            description: 'Hente kortform for vegsystemreferanse'
        } as angular.IRequestConfig)
            .then(result => result.data.veglenkesekvens.kortform);
    }

    getRoute(start: string, end: string): IPromise<Route> {
        return this.$http<Route>({
            url: `${this.nvdbReadApiUrl}/beta/vegnett/rute?start=${start}&slutt=${end}&srid=wgs84`,
            headers: HEADERS_V3,
            method: 'GET',
            description: 'Hente rute mellom to steder på vegen'
        } as angular.IRequestConfig)
            .then(result => {
                const vegnettsrutesegmenter: PointFromVegSystemReferanse[] = [];
                const route: Route = {
                    vegnettsrutesegmenter
                };
                result.data.vegnettsrutesegmenter.forEach(routeSegment => route.vegnettsrutesegmenter.push(routeSegment));
                return route;
            });
    }

    getWktFromReference(reference: string): IPromise<string> {
        if (reference.includes('-')) {
            const startReference = reference.split('-')[0];
            const endReference = reference.split('-')[1];
            const startShortFormPromise = this.getShortFormFromSystemReference(startReference);
            const endShortFormPromise = this.getShortFormFromSystemReference(endReference);

            return Promise.all([startShortFormPromise, endShortFormPromise])
                .then(responses => {
                    const startShortForm = responses[0];
                    const endShortForm = responses[1];
                    return this.getRoute(startShortForm, endShortForm)
                        .then(result => {
                            const linestrings = result.vegnettsrutesegmenter.map(roadnetSegment => roadnetSegment.geometri.wkt);
                            return MapUtils.combineLineStringsToOneLineString(linestrings);
                        });
                });
        } else {
            return this.$http<PointFromVegSystemReferanse>({
                url: `${this.nvdbReadApiUrl}/veg?vegsystemreferanse=${reference}&srid=4326`,
                headers: HEADERS_V3,
                method: 'GET',
                description: 'Hente punkt fra vegsystemreferanse'
            } as angular.IRequestConfig)
                .then(result => result.data.geometri.wkt);
        }
    }
}
nvdbapi.$inject = ['$http', 'server'];
